import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const fallbackTranslate = (text, replacements = {}) => {
    let output = text;
    Object.entries(replacements).forEach(([key, value]) => {
        output = output.replace(new RegExp(key, 'g'), value);
    });
    return output;
};

const translate = (text, replacements = {}) => {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
        try {
            return window.__(text, replacements);
        } catch (error) {
            // ignore and fall back
        }
    }

    return fallbackTranslate(text, replacements);
};

const t = translate;

const defaultMeta = {
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 0,
    search: '',
};

const StreamLibraryPicker = ({
    initialStreams = [],
    initialMeta = defaultMeta,
    endpoints = {},
    csrfToken = '',
    onAdd,
    manageUrl = '',
    perPage = 8,
}) => {
    const [streams, setStreams] = useState(Array.isArray(initialStreams) ? initialStreams : []);
    const [meta, setMeta] = useState(() => ({ ...defaultMeta, per_page: perPage, ...initialMeta }));
    const [searchInput, setSearchInput] = useState(() => (initialMeta?.search ? String(initialMeta.search) : ''));
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const debounceRef = useRef(null);
    const listContainerRef = useRef(null);

    const apiFetch = useCallback(async (url, options = {}) => {
        const config = {
            headers: {
                Accept: 'application/json',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                ...options.headers,
            },
            credentials: 'same-origin',
            ...options,
        };

        const response = await fetch(url, config);
        if (!response.ok) {
            let payload;
            try {
                payload = await response.json();
            } catch (error) {
                // ignore and fall back
            }
            const error = new Error(payload?.message || response.statusText || 'Request failed');
            error.status = response.status;
            throw error;
        }

        return response.json();
    }, [csrfToken]);

    const fetchStreams = useCallback(async ({ search = '', page = 1, append = false } = {}) => {
        if (!endpoints.list) {
            return;
        }

        if (append) {
            if (loadingMore) {
                return;
            }
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const url = new URL(endpoints.list, window.location.origin);
            url.searchParams.set('per_page', perPage);
            if (page > 1) {
                url.searchParams.set('page', page);
            }
            if (search) {
                url.searchParams.set('search', search);
            }

            const result = await apiFetch(url.toString());
            const receivedStreams = Array.isArray(result?.data) ? result.data : [];
            const receivedMeta = {
                ...defaultMeta,
                per_page: perPage,
                ...(result?.meta || {}),
                search,
            };

            setMeta(receivedMeta);
            setStreams((previous) => {
                if (!append) {
                    return receivedStreams;
                }

                if (receivedStreams.length === 0) {
                    return previous;
                }

                const seen = new Set(previous.map((item) => item?.id));
                const merged = [...previous];
                receivedStreams.forEach((item) => {
                    if (!item || typeof item.id === 'undefined' || seen.has(item.id)) {
                        return;
                    }
                    merged.push(item);
                    seen.add(item.id);
                });
                return merged;
            });
        } catch (fetchError) {
            console.error('[StreamLibraryPicker] Failed to fetch streams', fetchError);
            setError(fetchError.message || t('Unable to load stream library.'));
        } finally {
            if (append) {
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, [apiFetch, endpoints.list, loadingMore, perPage]);

    const handleSearchChange = useCallback((event) => {
        const value = event.target.value;
        setSearchInput(value);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            const trimmed = value.trim();
            fetchStreams({ search: trimmed, page: 1, append: false });
        }, 400);
    }, [fetchStreams]);

    const currentPage = meta?.current_page || 1;
    const lastPage = meta?.last_page || 1;
    const activeSearchValue = typeof meta?.search === 'string' ? meta.search : searchInput.trim();

    const handleLoadMore = useCallback(() => {
        if (loading || loadingMore || currentPage >= lastPage) {
            return;
        }
        fetchStreams({ search: activeSearchValue, page: currentPage + 1, append: true });
    }, [activeSearchValue, currentPage, fetchStreams, lastPage, loading, loadingMore]);

    const handleWheelScroll = useCallback((event) => {
        const container = listContainerRef.current;
        if (!container || typeof event.deltaY !== 'number') {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollHeight <= clientHeight) {
            return;
        }

        const nextScrollTop = scrollTop + event.deltaY;
        const atTop = scrollTop <= 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

        const scrollingUpPastTop = event.deltaY < 0 && atTop;
        const scrollingDownPastBottom = event.deltaY > 0 && atBottom;

        if (scrollingUpPastTop || scrollingDownPastBottom) {
            return;
        }

        event.preventDefault();
        const maxScrollTop = Math.max(scrollHeight - clientHeight, 0);
        container.scrollTop = Math.max(0, Math.min(nextScrollTop, maxScrollTop));
    }, []);

    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    const handleAddClick = useCallback((stream) => {
        if (typeof onAdd === 'function') {
            onAdd(stream);
        }
    }, [onAdd]);

    const lastUpdatedLabel = useCallback((stream) => {
        if (!stream?.last_used_at) {
            return t('Never used');
        }
        try {
            return new Date(stream.last_used_at).toLocaleString();
        } catch (error) {
            return stream.last_used_at;
        }
    }, []);

    const emptyState = useMemo(() => {
        if (loading) {
            return null;
        }
        if (streams.length > 0) {
            return null;
        }
        if (searchInput.trim()) {
            return t('No saved streams match this search.');
        }
        return t('You have no saved streams yet. Use Stream Management to add some and reuse them here.');
    }, [loading, searchInput, streams.length]);

    const perPageValue = meta?.per_page || perPage;
    const hasMore = currentPage < lastPage;
    const showSearchHint = (meta?.total || 0) > perPageValue;

    return (
        <Card variant="outlined">
            <CardHeader
                title={t('Saved Streams Library')}
                subheader={t('Quickly attach existing streams to this wall without retyping URLs.')}
                action={manageUrl ? (
                    <Tooltip title={t('Open Stream Management in a new tab')}>
                        <IconButton component="a" href={manageUrl} target="_blank" rel="noopener noreferrer">
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : null}
            />
            <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} mb={2}>
                    <TextField
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder={t('Search library streams')}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {t(':count saved streams', { ':count': meta.total ?? streams.length })}
                    </Typography>
                </Stack>

                {showSearchHint && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {t('Tip: type to filter if the list feels long.')}
                    </Typography>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Box py={3} display="flex" justifyContent="center">
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && streams.length > 0 && (
                    <>
                        <Box
                            ref={listContainerRef}
                            sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}
                            onWheel={handleWheelScroll}
                        >
                            <List dense disablePadding>
                                {streams.map((stream) => (
                                    <ListItem key={stream.id} divider disableGutters>
                                        <ListItemText
                                            primary={stream.name || t('Untitled stream')}
                                            secondary={
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                                                        {stream.url}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.disabled">
                                                        {t('Last used: :date', { ':date': lastUpdatedLabel(stream) })}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title={t('Add to wall')}>
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        startIcon={<AddIcon />}
                                                        onClick={() => handleAddClick(stream)}
                                                    >
                                                        {t('Add')}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>

                        {hasMore && (
                            <Box display="flex" justifyContent="center" mt={2}>
                                <Button variant="outlined" color="primary" size="small" onClick={handleLoadMore} disabled={loadingMore}>
                                    {loadingMore && <CircularProgress size={18} sx={{ mr: 1 }} />}
                                    {loadingMore ? t('Loading...') : t('Load more')}
                                </Button>
                            </Box>
                        )}
                    </>
                )}

                {emptyState && (
                    <Box py={3} textAlign="center">
                        <Typography variant="body2" color="text.secondary">
                            {emptyState}
                        </Typography>
                        {manageUrl && (
                            <Button
                                href={manageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                color="primary"
                                size="small"
                                sx={{ mt: 1.5 }}
                            >
                                {t('Open Stream Management')}
                            </Button>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

StreamLibraryPicker.propTypes = {
    initialStreams: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        url: PropTypes.string,
        last_used_at: PropTypes.string,
    })),
    initialMeta: PropTypes.object,
    endpoints: PropTypes.shape({
        list: PropTypes.string,
    }),
    csrfToken: PropTypes.string,
    onAdd: PropTypes.func,
    manageUrl: PropTypes.string,
    perPage: PropTypes.number,
};

export default StreamLibraryPicker;
